import React from "react";
import { motion } from "framer-motion";
import { FaArrowRight, FaCode, FaBullhorn, FaPenNib, FaSearch, FaShoppingCart } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import "./Services.css";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const Services = () => {
  const navigate = useNavigate();

  const services = [
    {
      tag: "Build",
      title: "Website Development",
      desc: "Custom, responsive websites built for speed, SEO, and conversions — from landing pages to full business sites.",
      icon: <FaCode />,
      bullets: ["React & WordPress", "Mobile-first design", "Fast hosting setup"],
    },
    {
      tag: "Design",
      title: "UI/UX Design",
      desc: "Clean, modern interfaces that build trust and guide visitors toward taking action on your site.",
      icon: <FaPenNib />,
      bullets: ["Wireframes & mockups", "Brand consistency", "Conversion-focused layouts"],
    },
    {
      tag: "Visibility",
      title: "SEO & Content",
      desc: "Get found on Google with technical SEO, keyword strategy, and content that ranks and converts.",
      icon: <FaSearch />,
      bullets: ["On-page optimization", "Content strategy", "Local SEO"],
    },
    {
      tag: "Reach",
      title: "Digital Marketing",
      desc: "Targeted ad campaigns and social media strategies that bring qualified leads to your business.",
      icon: <FaBullhorn />,
      bullets: ["Google & Meta ads", "Social media management", "Email campaigns"],
    },
    {
      tag: "Sell",
      title: "E-Commerce Setup",
      desc: "Online stores with secure payments, inventory management, and checkout flows optimized for sales.",
      icon: <FaShoppingCart />,
      bullets: ["Shopify & WooCommerce", "Payment integration", "Product pages"],
    },
    {
      tag: "Plan",
      title: "Digital Consulting",
      desc: "Strategic guidance on what to build, how to position your brand, and where to invest for maximum ROI.",
      icon: <FaArrowRight />,
      bullets: ["Competitor analysis", "Growth roadmap", "KPI tracking"],
    },
  ];

  const stats = [
    { num: "50+", label: "Projects delivered" },
    { num: "95%", label: "Client retention" },
    { num: "10–21d", label: "Typical turnaround" },
    { num: "24/7", label: "Help available" },
  ];

  return (
    <section className="srv2" aria-label="Services">
      <div className="srv2__wrap">
        <motion.div
          className="srv2__head"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.35 }}
          variants={fadeUp}
        >
          <div>
            <div className="srv2__badge">
              <span className="dot" aria-hidden="true" />
              Our Services
            </div>
            <h2 className="srv2__title">
              Everything your business needs to <span>succeed online</span>.
            </h2>
            <p className="srv2__sub">
              Choose a single service or a complete digital package. We focus on
              quality, transparency, and results you can measure.
            </p>
          </div>
          <div className="srv2__aside">
            <b>Not sure where to start?</b>
            <p>Book a free consultation — we'll recommend the best path for your goals.</p>
          </div>
        </motion.div>

        <div className="srv2__grid">
          {services.map((s, i) => (
            <motion.div
              key={i}
              className="srv2__card"
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeUp}
              transition={{ delay: i * 0.05 }}
              onClick={() => navigate("/contact")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && navigate("/contact")}
            >
              <div className="srv2__top">
                <div className="srv2__icon" aria-hidden="true">{s.icon}</div>
                <div className="srv2__tag">{s.tag}</div>
              </div>
              <h3 className="srv2__h3">{s.title}</h3>
              <p className="srv2__p">{s.desc}</p>
              <ul className="srv2__bullets">
                {s.bullets.map((b, idx) => <li key={idx}>{b}</li>)}
              </ul>
              <span className="srv2__link">Get a quote <FaArrowRight /></span>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="srv2__stats"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.25 }}
          variants={fadeUp}
        >
          <div className="srv2__statsGrid">
            {stats.map((st, i) => (
              <div className="srv2__stat" key={i}>
                <b>{st.num}</b>
                <span>{st.label}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
